import { toastError } from "./pageUtil/toast";
import {
  sanitizeFilenameForS3,
  sanitizeForOpenSearch,
} from "./sanitizeFilenameForS3";
import { PresignedPost } from "./types/PresignedPost";
import { Coordinate, GfImage } from "@swagger/typescript-fetch-goflyer";

/**
 * Response from the signed S3 URL request.
 *
 * @property signedUrl - Pre-signed POST data for uploading to S3
 * @property filename - Original filename with unique identifier
 * @property fileKey - S3 object key for the uploaded file
 * @property publicUrl - Public URL to access the uploaded file
 */
type SignedRequestResponse = {
  signedUrl: PresignedPost;
  filename: string;
  fileKey: string;
  publicUrl: string;
};

/**
 * Uploads an image file to S3 using a pre-signed POST request.
 *
 * Constructs a FormData with the signed URL fields and uploads the file.
 * Returns image metadata including alt text, source URL, and filename.
 * Uses the original filename for GfImage filename, not the modified filename with unique identifier.
 *
 * @param file - The image file to upload
 * @param jsonResult - Pre-signed POST data from getSignedRequest
 * @returns Object with alt, src, and fileName if successful, null on error
 */
async function uploadImageFile(
  file: File,
  jsonResult: SignedRequestResponse,
): Promise<{ alt: string; src: string; fileName: string } | null> {
  const formParams = jsonResult.signedUrl;
  const formData = new FormData();
  Object.keys(formParams.fields).forEach((key) => {
    formData.append(key, formParams.fields[key]);
  });
  formData.append("file", file);

  const response = await fetch(formParams.url, {
    method: "POST",
    body: formData,
  });
  if (response.status === 204) {
    console.log("File uploaded successfully, no content returned");
  } else if (response.status === 400) {
    toastError(
      `Unable to upload ${jsonResult.filename}: Image size exceeds the 10MB limit.`,
    );
    return null;
  } else if (!response.ok) {
    toastError(`Unable to upload ${jsonResult.filename} this.`);
    console.error(`${response.status}: ${response.statusText}`);
    return null;
  }

  const originalFilename = file.name;
  const originalAlt = originalFilename.substring(
    0,
    originalFilename.lastIndexOf("."),
  );

  const imageAlt = sanitizeForOpenSearch(originalAlt);
  return {
    alt: imageAlt,
    fileName: originalAlt,
    src: jsonResult?.publicUrl,
  };
}

/**
 * Requests a pre-signed POST URL from the backend for S3 upload.
 *
 * Generates a unique 13-digit identifier and appends it to the filename to prevent collisions.
 * Sanitizes the filename to ensure S3 compatibility.
 *
 * @param file - The file to generate a signed request for
 * @param small - Whether to prefix filename with "small-" for thumbnail variants
 * @param path - S3 path prefix (default: "flyers")
 * @returns Pre-signed POST data including URL, fields, and public URL
 * @throws Error if the API request fails
 */
async function getSignedRequest(
  file: File,
  small: boolean,
  path = `flyers`,
): Promise<SignedRequestResponse> {
  const unique13digitNumber = Math.floor(
    1000000000000 + Date.now() + Math.random() * 9000000000000,
  );

  const sanitizedFileName = sanitizeFilenameForS3(file.name, true);
  const n = sanitizedFileName.lastIndexOf(".");
  const fileName = encodeURIComponent(
    `${sanitizedFileName.substring(
      0,
      n,
    )}_${unique13digitNumber}${sanitizedFileName.substring(n)}`,
  );

  const prefix = small ? "small-" : "";
  const url = `${process.env.REACT_APP_GOFLYER_API_END_POINT}/s3/getPresignedPostUrl?objectName=${prefix}${fileName}&contentType=${file.type}&path=${path}`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Complete upload workflow: gets signed URL and uploads image to S3.
 *
 * Combines getSignedRequest and uploadImageFile into a single operation.
 * Handles errors gracefully and returns null on failure.
 *
 * @param file - The image file to upload
 * @param small - Whether to use "small-" prefix for thumbnails
 * @param path - S3 path prefix (default: "flyers")
 * @returns Object with alt, src, and fileName if successful, null on error
 */
async function uploadImageToS3(
  file: File,
  small: boolean,
  path = `flyers`,
): Promise<{ alt: string; src: string; fileName: string } | null> {
  try {
    const json = await getSignedRequest(file, small, path);
    return await uploadImageFile(file, json);
  } catch (err) {
    console.error(err);
    return null;
  }
}

/**
 * Loads an image via fetch to bypass CORS restrictions.
 *
 * Returns a blob URL that can be used for canvas operations without CORS issues.
 * For data URLs and blob URLs, returns them unchanged.
 * Falls back to original URL if fetch fails due to CORS, network errors, or other issues.
 *
 * @param imageUrl - URL of the image to load
 * @returns Blob URL for CORS-free access, or original URL as fallback
 */
async function loadImageWithCORS(imageUrl: string): Promise<string> {
  if (imageUrl.startsWith("data:") || imageUrl.startsWith("blob:")) {
    return imageUrl;
  }

  try {
    const response = await fetch(imageUrl, {
      mode: "cors",
      credentials: "omit",
      cache: "no-cache",
    });

    if (response.ok) {
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    }
  } catch (error) {
    console.log("Error fetching image with CORS:", error);
  }

  return imageUrl;
}

/**
 * Crops an image based on coordinate data and returns a File object ready for upload.
 *
 * Creates a canvas with dimensions matching the coordinate bounding box, draws the cropped
 * portion of the image, and converts it to a PNG File. Handles CORS issues by setting
 * crossOrigin attribute and falls back to loadImageWithCORS if initial load fails.
 *
 * @param imageSrc - Source URL or blob URL of the image to crop
 * @param coordinate - Coordinate object defining crop area (x, y, width, height)
 * @param fileName - Desired filename for the resulting File
 * @param cleanupBlobUrl - Whether to revoke blob URL after cropping (default: false)
 * @returns Promise resolving to a File object containing the cropped image
 * @throws Error if canvas context cannot be obtained or cropping fails
 */
async function cropImage(
  imageSrc: string,
  coordinate: Coordinate,
  fileName: string,
  cleanupBlobUrl = false,
): Promise<File> {
  return new Promise(async (resolve, reject) => {
    const img = new Image();

    img.crossOrigin = "anonymous";

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error("Could not get canvas context"));
          return;
        }

        const width = coordinate.component.width;
        const height = coordinate.component.height;

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(
          img,
          coordinate.x,
          coordinate.y,
          width,
          height,
          0,
          0,
          width,
          height,
        );

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Failed to create blob from canvas"));
              return;
            }

            const file = new File([blob], fileName, {
              type: "image/png",
              lastModified: Date.now(),
            });

            if (cleanupBlobUrl && imageSrc.startsWith("blob:")) {
              URL.revokeObjectURL(imageSrc);
            }

            resolve(file);
          },
          "image/png",
          1.0,
        );
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = async () => {
      try {
        const corsImageSrc = await loadImageWithCORS(imageSrc);
        img.src = corsImageSrc;
      } catch (error) {
        reject(new Error(`Failed to load image: ${imageSrc}`));
      }
    };

    img.src = imageSrc;
  });
}

/**
 * Finds the GfImage for a specific flyer page by flyerIndex.
 *
 * @param gfImages - Array of GfImage objects to search
 * @param flyerIndex - The flyer page index to find
 * @returns The matching GfImage, or undefined if not found
 */
function getFlyerImageForPage(
  gfImages: GfImage[],
  flyerIndex: number,
): GfImage | undefined {
  return gfImages.find((img) => img.flyerIndex === flyerIndex);
}

export {
  getSignedRequest,
  uploadImageToS3,
  uploadImageFile,
  loadImageWithCORS,
  cropImage,
  getFlyerImageForPage,
};
